// Lazy-loaded Sentry and GA references (not imported at top level to avoid
// pulling ~200KB+ into the initial bundle via transitive imports).
let _Sentry = null;
let _ReactGA = null;

const getSentry = async () => {
  if (_Sentry) return _Sentry;
  try {
    _Sentry = await import('@sentry/react');
  } catch (e) {
    console.warn('Failed to load Sentry:', e);
    _Sentry = null;
  }
  return _Sentry;
};

const getReactGA = async () => {
  if (_ReactGA) return _ReactGA;
  try {
    const mod = await import('react-ga4');
    _ReactGA = mod.default || mod;
  } catch (e) {
    console.warn('Failed to load react-ga4:', e);
    _ReactGA = null;
  }
  return _ReactGA;
};

import { onCLS, onINP, onFCP, onLCP, onTTFB } from 'web-vitals';

// Helper to check if user is authenticated
const isAuthenticated = () => {
  try {
    if (typeof window === 'undefined') return false;
    return !!(
      localStorage.getItem('access_token') ||
      localStorage.getItem('patientData') ||
      localStorage.getItem('clinicData') ||
      localStorage.getItem('userProfile')
    );
  } catch (e) {
    return false;
  }
};

// Helper to check if current path is a marketing/public page
const isMarketingPage = () => {
  try {
    if (typeof window === 'undefined') return false;
    const path = window.location.pathname.toLowerCase();
    // Marketing pages and public pages (no authentication required)
    const marketingPaths = [
      '/marketing',
      '/about',
      '/contact',
      '/product',
      '/specialties',
      '/case-studies',
      '/comparison',
      '/roi-calculator',
      '/blog',
      '/faq',
      '/privacy',
      '/terms',
      '/',
      '/login',
      '/register',
      '/patient-registration',
      '/patient-login',
      '/forgot-password',
      '/forgot-username',
    ];
    // Check if path starts with any marketing path
    return marketingPaths.some(marketingPath => 
      path === marketingPath || path.startsWith(marketingPath + '/')
    ) && !path.startsWith('/dashboard') && !path.startsWith('/patient-dashboard');
  } catch (e) {
    return false;
  }
};

// Check if Google Analytics should be enabled (only for marketing pages, not authenticated)
const isGAEnabled = () => {
  return !isAuthenticated() && isMarketingPage() && !!process.env.REACT_APP_GA_MEASUREMENT_ID;
};

// Initialize monitoring services
export const initializeMonitoring = async () => {
  const allowPatientMonitoring = process.env.REACT_APP_ALLOW_PATIENT_MONITORING === 'true';
  const isPatientUser = (() => {
    try {
      return typeof window !== 'undefined' && !!localStorage.getItem('patientData');
    } catch (e) {
      return false;
    }
  })();
  const monitoringEnabledForUser = !isPatientUser || allowPatientMonitoring;
  
  // Initialize Sentry for error tracking
  // Note: Sentry offers BAAs on Business+ plans for HIPAA compliance
  // WITHOUT a BAA: Only enable Sentry on marketing pages (public, no PHI risk)
  // WITH a BAA: You can enable Sentry in authenticated areas by setting REACT_APP_SENTRY_ENABLE_AUTHENTICATED=true
  const hasSentryBAA = process.env.REACT_APP_SENTRY_ENABLE_AUTHENTICATED === 'true';
  const isSentryEnabled = () => {
    if (!process.env.REACT_APP_SENTRY_DSN) return false;
    // Always allow on marketing pages (public, no PHI)
    if (isMarketingPage() && !isAuthenticated()) return true;
    // In authenticated areas, only enable if BAA is confirmed
    if (hasSentryBAA && monitoringEnabledForUser) return true;
    // Otherwise disable (no BAA, authenticated area = PHI risk)
    return false;
  };
  
  if (isSentryEnabled()) {
    const Sentry = await getSentry();
    const tracingMod = await import('@sentry/tracing').catch(() => null);
    if (Sentry) {
      const integrations = [];
      if (tracingMod?.BrowserTracing) {
        integrations.push(new tracingMod.BrowserTracing());
      }
      Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_DSN,
        integrations,
        tracesSampleRate: 1.0,
        environment: process.env.NODE_ENV,
        beforeSend(event) {
          // Filter out certain errors in development
          if (process.env.NODE_ENV === 'development' && event.exception) {
            const errorMessage = event.exception.values?.[0]?.value || '';
            if (errorMessage.includes('ResizeObserver loop limit exceeded')) {
              return null; // Ignore common development error
            }
          }
          
          // Scrub potential PHI from Sentry events
          if (event) {
            // Scrub URLs that might contain PHI
            if (event.request && event.request.url) {
              try {
                const url = new URL(event.request.url);
                const safeParams = new URLSearchParams();
                const allowedParams = ['page', 'tab', 'view', 'filter', 'sort'];
                url.searchParams.forEach((value, key) => {
                  if (allowedParams.includes(key.toLowerCase())) {
                    safeParams.set(key, value);
                  }
                });
                url.search = safeParams.toString();
                event.request.url = url.toString();
              } catch (e) {
                delete event.request.url;
              }
            }
            
            // Scrub user context if it might contain PHI
            if (event.user) {
              const safeUser = {
                id: event.user.id,
              };
              if (isMarketingPage() && !isAuthenticated()) {
                safeUser.username = event.user.username;
                safeUser.email = event.user.email;
              }
              event.user = safeUser;
            }
            
            // Scrub breadcrumbs that might contain PHI
            if (event.breadcrumbs) {
              event.breadcrumbs = event.breadcrumbs.map(crumb => {
                const scrubbed = { ...crumb };
                if (scrubbed.message) {
                  scrubbed.message = scrubbed.message.replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[email redacted]');
                  scrubbed.message = scrubbed.message.replace(/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g, '[phone redacted]');
                }
                return scrubbed;
              });
            }
          }
          
          return event;
        },
      });
    }
  }

  // Initialize Google Analytics ONLY for marketing pages when NOT authenticated
  // GA4 is not HIPAA-compliant, so we must never use it in authenticated areas
  if (isGAEnabled()) {
    const ReactGA = await getReactGA();
    if (ReactGA) {
      ReactGA.initialize(process.env.REACT_APP_GA_MEASUREMENT_ID);
    }
  }

  // Initialize Web Vitals monitoring (only send to GA if GA is enabled)
  if (monitoringEnabledForUser) {
    initializeWebVitals();
  }
};

// Web Vitals monitoring
const initializeWebVitals = () => {
  const sendToAnalytics = ({ name, delta, id }) => {
    // Send to Google Analytics ONLY if GA is enabled (marketing pages, not authenticated)
    if (isGAEnabled() && _ReactGA) {
      _ReactGA.event({
        category: 'Web Vitals',
        action: name,
        value: Math.round(name === 'CLS' ? delta * 1000 : delta),
        label: id,
        nonInteraction: true,
      });
    }

    // Send to Sentry as breadcrumb - only if Sentry is loaded and enabled
    const isPatientUser = (() => {
      try { return typeof window !== 'undefined' && !!localStorage.getItem('patientData'); } catch { return false; }
    })();
    if (_Sentry && process.env.REACT_APP_SENTRY_DSN && ((isMarketingPage() && !isAuthenticated()) || (process.env.REACT_APP_SENTRY_ENABLE_AUTHENTICATED === 'true' && !isPatientUser))) {
      _Sentry.addBreadcrumb({
        category: 'web-vitals',
        message: `${name}: ${delta}`,
        level: 'info',
        data: { metric: name, value: delta, id },
      });
    }

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`Web Vital: ${name} = ${delta}`);
    }
  };

  onCLS(sendToAnalytics);
  onINP(sendToAnalytics);
  onFCP(sendToAnalytics);
  onLCP(sendToAnalytics);
  onTTFB(sendToAnalytics);
};

// Custom error tracking
export const trackError = (error, context = {}) => {
  const allowPatientMonitoring = process.env.REACT_APP_ALLOW_PATIENT_MONITORING === 'true';
  const isPatientUser = (() => {
    try { return typeof window !== 'undefined' && !!localStorage.getItem('patientData'); } catch { return false; }
  })();
  if (isPatientUser && !allowPatientMonitoring) return;
  console.error('Application Error:', error, context);
  
  // Only send to Sentry if enabled (marketing pages or authenticated with BAA)
  const hasSentryBAA = process.env.REACT_APP_SENTRY_ENABLE_AUTHENTICATED === 'true';
  const canUseSentry = (isMarketingPage() && !isAuthenticated()) || (hasSentryBAA && !isPatientUser);
  
  if (process.env.REACT_APP_SENTRY_DSN && canUseSentry) {
    // Use already-loaded Sentry if available, otherwise load it
    if (_Sentry) {
      _Sentry.captureException(error, { extra: context });
    } else {
      getSentry().then(Sentry => {
        if (Sentry) Sentry.captureException(error, { extra: context });
      });
    }
  }
};

// Custom event tracking
export const trackEvent = (category, action, label = null, value = null) => {
  const allowPatientMonitoring = process.env.REACT_APP_ALLOW_PATIENT_MONITORING === 'true';
  const isPatientUser = (() => {
    try { return typeof window !== 'undefined' && !!localStorage.getItem('patientData'); } catch { return false; }
  })();
  if (isPatientUser && !allowPatientMonitoring) return;
  
  // Google Analytics - ONLY for marketing pages when NOT authenticated (HIPAA compliance)
  if (isGAEnabled() && _ReactGA) {
    _ReactGA.event({
      category,
      action,
      label,
      value,
    });
  }

  // Sentry - only on marketing pages or authenticated with BAA
  const hasSentryBAA = process.env.REACT_APP_SENTRY_ENABLE_AUTHENTICATED === 'true';
  const canUseSentry = (isMarketingPage() && !isAuthenticated()) || (hasSentryBAA && !isPatientUser);
  
  if (_Sentry && process.env.REACT_APP_SENTRY_DSN && canUseSentry) {
    _Sentry.addBreadcrumb({
      category,
      message: `${action}${label ? `: ${label}` : ''}`,
      level: 'info',
    });
  }

  // Log in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`Event: ${category} - ${action}${label ? ` (${label})` : ''}`);
  }
};

// Page view tracking
export const trackPageView = (path) => {
  const allowPatientMonitoring = process.env.REACT_APP_ALLOW_PATIENT_MONITORING === 'true';
  const isPatientUser = (() => {
    try { return typeof window !== 'undefined' && !!localStorage.getItem('patientData'); } catch { return false; }
  })();
  if (isPatientUser && !allowPatientMonitoring) return;
  
  // Google Analytics - ONLY for marketing pages when NOT authenticated (HIPAA compliance)
  if (isGAEnabled() && _ReactGA) {
    _ReactGA.send({ hitType: 'pageview', page: path });
  }

  // Sentry - only on marketing pages or authenticated with BAA
  const hasSentryBAA = process.env.REACT_APP_SENTRY_ENABLE_AUTHENTICATED === 'true';
  const canUseSentry = (isMarketingPage() && !isAuthenticated()) || (hasSentryBAA && !isPatientUser);
  
  if (_Sentry && process.env.REACT_APP_SENTRY_DSN && canUseSentry) {
    _Sentry.addBreadcrumb({
      category: 'navigation',
      message: `Page view: ${path}`,
      level: 'info',
    });
  }
};

// Performance monitoring
export const trackPerformance = (name, duration, metadata = {}) => {
  const allowPatientMonitoring = process.env.REACT_APP_ALLOW_PATIENT_MONITORING === 'true';
  const isPatientUser = (() => {
    try { return typeof window !== 'undefined' && !!localStorage.getItem('patientData'); } catch { return false; }
  })();
  if (isPatientUser && !allowPatientMonitoring) return;
  
  // Sentry - only on marketing pages or authenticated with BAA
  const hasSentryBAA = process.env.REACT_APP_SENTRY_ENABLE_AUTHENTICATED === 'true';
  const canUseSentry = (isMarketingPage() && !isAuthenticated()) || (hasSentryBAA && !isPatientUser);
  
  if (_Sentry && process.env.REACT_APP_SENTRY_DSN && canUseSentry) {
    _Sentry.addBreadcrumb({
      category: 'performance',
      message: `${name}: ${duration}ms`,
      level: 'info',
      data: { duration, ...metadata },
    });
  }

  // Google Analytics - ONLY for marketing pages when NOT authenticated (HIPAA compliance)
  if (isGAEnabled() && _ReactGA) {
    _ReactGA.event({
      category: 'Performance',
      action: name,
      value: Math.round(duration),
      label: JSON.stringify(metadata),
    });
  }
};

// User behavior tracking
export const trackUserAction = (action, details = {}) => {
  const allowPatientMonitoring = process.env.REACT_APP_ALLOW_PATIENT_MONITORING === 'true';
  const isPatientUser = (() => {
    try { return typeof window !== 'undefined' && !!localStorage.getItem('patientData'); } catch { return false; }
  })();
  if (isPatientUser && !allowPatientMonitoring) return;
  trackEvent('User Action', action, null, null);
  
  // Set Sentry user context - only if Sentry is loaded and enabled
  const hasSentryBAA = process.env.REACT_APP_SENTRY_ENABLE_AUTHENTICATED === 'true';
  const canUseSentry = (isMarketingPage() && !isAuthenticated()) || (hasSentryBAA && !isPatientUser);
  
  if (_Sentry && process.env.REACT_APP_SENTRY_DSN && canUseSentry && !isPatientUser) {
    const safeUserContext = {
      id: details.userId,
    };
    
    if (isMarketingPage() && !isAuthenticated()) {
      safeUserContext.username = details.username;
    }
    
    if (details.clinic_id && !isPatientUser) {
      safeUserContext.clinic_id = details.clinic_id;
    }
    
    _Sentry.setUser(safeUserContext);
  }
};

// API call monitoring
export const trackApiCall = (endpoint, method, duration, status, error = null) => {
  const allowPatientMonitoring = process.env.REACT_APP_ALLOW_PATIENT_MONITORING === 'true';
  const isPatientUser = (() => {
    try { return typeof window !== 'undefined' && !!localStorage.getItem('patientData'); } catch { return false; }
  })();
  if (isPatientUser && !allowPatientMonitoring) return;
  const success = status >= 200 && status < 300;
  
  trackEvent('API Call', `${method} ${endpoint}`, status.toString(), duration);
  
  if (!success && error) {
    trackError(error, {
      endpoint,
      method,
      status,
      duration,
    });
  }
};

// Custom metrics
export const trackMetric = (name, value, tags = {}) => {
  const allowPatientMonitoring = process.env.REACT_APP_ALLOW_PATIENT_MONITORING === 'true';
  const isPatientUser = (() => {
    try { return typeof window !== 'undefined' && !!localStorage.getItem('patientData'); } catch { return false; }
  })();
  if (isPatientUser && !allowPatientMonitoring) return;
  
  // Sentry - only on marketing pages or authenticated with BAA
  const hasSentryBAA = process.env.REACT_APP_SENTRY_ENABLE_AUTHENTICATED === 'true';
  const canUseSentry = (isMarketingPage() && !isAuthenticated()) || (hasSentryBAA && !isPatientUser);
  
  if (_Sentry && process.env.REACT_APP_SENTRY_DSN && canUseSentry) {
    _Sentry.addBreadcrumb({
      category: 'metrics',
      message: `${name}: ${value}`,
      level: 'info',
      data: { value, tags },
    });
  }
};

// Session monitoring
export const trackSession = (action, details = {}) => {
  const allowPatientMonitoring = process.env.REACT_APP_ALLOW_PATIENT_MONITORING === 'true';
  const isPatientUser = (() => {
    try { return typeof window !== 'undefined' && !!localStorage.getItem('patientData'); } catch { return false; }
  })();
  if (isPatientUser && !allowPatientMonitoring) return;
  trackEvent('Session', action, null, null);
  
  // Sentry - only on marketing pages or authenticated with BAA
  const hasSentryBAA = process.env.REACT_APP_SENTRY_ENABLE_AUTHENTICATED === 'true';
  const canUseSentry = (isMarketingPage() && !isAuthenticated()) || (hasSentryBAA && !isPatientUser);
  
  if (_Sentry && process.env.REACT_APP_SENTRY_DSN && canUseSentry) {
    _Sentry.addBreadcrumb({
      category: 'session',
      message: `Session ${action}: ${JSON.stringify(details)}`,
      level: 'info',
    });
  }
};
